/**
 * Application Routes Configuration
 * React Router 7 - Client Side Rendering
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { MainLayout, AuthLayout } from '../components/layout';
import { LoginPage, ForgotPasswordPage, VerifyResetOTPPage, ResetPasswordPage, NoPermissionsPage } from '../pages/auth';
import { DashboardPage } from '../pages/dashboard';
import { OrderDetailPage, OrdersTabsPage } from '../pages/orders';
import { ReviewsListPage } from '../pages/reviews';
import { ProfilePage } from '../pages/profile';
import { ServicesListPage, ServiceDetailPage, ServiceFormPage } from '../pages/services';
import { SubservicesListPage, SubserviceDetailPage, SubserviceFormPage, InternalSubservicesListPage, InternalSubserviceDetailPage } from '../pages/subservices';
import { UsersListPage, PatientDetailPage, PatientFormPage } from '../pages/users';
import { PaymentDetailPage, PaymentsTabsPage } from '../pages/payments';
import { SpecialistsListPage, SpecialistDetailPage, SpecialistFormPage } from '../pages/specialists';
import { ConsultantsListPage, ConsultantDetailPage, ConsultantFormPage } from '../pages/consultants';
import { ReportsPage } from '../pages/reports';
import { SettingsPage } from '../pages/settings';
import { AdsListPage, AdFormPage, AdDetailPage } from '../pages/ads';
import { TermsListPage, TermsFormPage, TermsDetailPage } from '../pages/terms';
import { RolesListPage, RoleDetailPage, RoleFormPage, EmployeesListPage, EmployeeDetailPage, EmployeeFormPage } from '../pages/rbac';
import {
  CategoriesListPage,
  CategoryFormPage,
  SubCategoriesListPage,
  SubCategoryFormPage,
  ProductsListPage,
  ProductFormPage,
  ProductDetailPage,
  MarketplaceOrdersListPage,
  MarketplaceOrderDetailPage,
  MarketplacePaymentsListPage,
} from '../pages/marketplace';
import { ErrorPage } from '../pages/error';
import ProtectedRoute from './ProtectedRoute';
import PermissionRoute from './PermissionRoute';


export const router = createBrowserRouter([
  // Auth routes
  {
    path: '/login',
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
    ],
  },
  {
    path: '/forgot-password',
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <ForgotPasswordPage />,
      },
    ],
  },
  {
    path: '/verify-reset-otp',
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <VerifyResetOTPPage />,
      },
    ],
  },
  {
    path: '/reset-password',
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    path: '/no-permissions',
    element: <NoPermissionsPage />,
    errorElement: <ErrorPage />,
  },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <PermissionRoute permission="view_dashboard">
            <DashboardPage />
          </PermissionRoute>
        ),
      },
      // Orders
      {
        path: 'orders',
        children: [
          {
            index: true,
            element: <OrdersTabsPage />,
          },
          {
            path: ':id',
            element: <OrderDetailPage />,
          },
        ],
      },
      // Order reviews / ratings
      {
        path: 'reviews',
        element: (
          <PermissionRoute permission="view_reviews">
            <ReviewsListPage />
          </PermissionRoute>
        ),
      },
      // Services
      {
        path: 'services',
        children: [
          {
            index: true,
            element: <ServicesListPage />,
          },
          {
            path: 'create',
            element: <ServiceFormPage />,
          },
          {
            path: ':id',
            element: <ServiceDetailPage />,
          },
          {
            path: ':id/edit',
            element: <ServiceFormPage />,
          },
        ],
      },
      // Subservices
      {
        path: 'subservices',
        children: [
          {
            index: true,
            element: <SubservicesListPage />,
          },
          {
            path: 'create',
            element: <SubserviceFormPage />,
          },
          {
            path: ':id',
            element: <SubserviceDetailPage />,
          },
          {
            path: ':id/edit',
            element: <SubserviceFormPage />,
          },
          {
            path: ':parentId/internal/create',
            element: <SubserviceFormPage />,
          },
          {
            path: ':parentId/internal/:id/edit',
            element: <SubserviceFormPage />,
          },
        ],
      },
      // Internal Subservices
      {
        path: 'internal-subservices',
        children: [
          {
            index: true,
            element: <InternalSubservicesListPage />,
          },
          {
            path: 'create',
            element: <SubserviceFormPage />,
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: <InternalSubserviceDetailPage />,
              },
              {
                path: 'edit',
                element: <SubserviceFormPage />,
              },
            ],
          },
        ],
      },
      // Users
      {
        path: 'users',
        children: [
          {
            index: true,
            element: <UsersListPage />,
          },
          {
            path: 'patients/create',
            element: (
              <PermissionRoute permission="manage_patients">
                <PatientFormPage />
              </PermissionRoute>
            ),
          },
          {
            path: 'patients/:id',
            element: <PatientDetailPage />,
          },
          {
            path: 'patients/:id/edit',
            element: (
              <PermissionRoute permission="manage_patients">
                <PatientFormPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'specialists',
        children: [
          {
            index: true,
            element: <SpecialistsListPage />,
          },
          {
            path: 'create',
            element: (
              <PermissionRoute permission="manage_specialists">
                <SpecialistFormPage />
              </PermissionRoute>
            ),
          },
          {
            path: ':id',
            element: <SpecialistDetailPage />,
          },
          {
            path: ':id/edit',
            element: (
              <PermissionRoute permission="manage_specialists">
                <SpecialistFormPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'consultants',
        children: [
          {
            index: true,
            element: <ConsultantsListPage />,
          },
          {
            path: 'create',
            element: (
              <PermissionRoute permission="manage_consultants">
                <ConsultantFormPage />
              </PermissionRoute>
            ),
          },
          {
            path: ':id',
            element: <ConsultantDetailPage />,
          },
          {
            path: ':id/edit',
            element: (
              <PermissionRoute permission="manage_consultants">
                <ConsultantFormPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      // Finance
      {
        path: 'payments',
        children: [
          {
            index: true,
            element: <PaymentsTabsPage />,
          },
          {
            path: ':id',
            element: <PaymentDetailPage />,
          },
        ],
      },
      {
        path: 'reports',
        element: <ReportsPage />,
      },
      // Ads
      {
        path: 'ads',
        children: [
          {
            index: true,
            element: <AdsListPage />,
          },
          {
            path: 'create',
            element: <AdFormPage />,
          },
          {
            path: ':id/edit',
            element: <AdFormPage />,
          },
          {
            path: ':id',
            element: <AdDetailPage />,
          },
        ],
      },
      // Terms and Conditions
      {
        path: 'terms',
        children: [
          {
            index: true,
            element: <TermsListPage />,
          },
          {
            path: 'create',
            element: <TermsFormPage />,
          },
          {
            path: ':id/edit',
            element: <TermsFormPage />,
          },
          {
            path: ':id',
            element: <TermsDetailPage />,
          },
        ],
      },
      // RBAC
      {
        path: 'roles',
        children: [
          {
            index: true,
            element: <RolesListPage />,
          },
          {
            path: 'create',
            element: <RoleFormPage />,
          },
          {
            path: ':id',
            element: <RoleDetailPage />,
          },
          {
            path: ':id/edit',
            element: <RoleFormPage />,
          },
        ],
      },
      {
        path: 'employees',
        children: [
          {
            index: true,
            element: <EmployeesListPage />,
          },
          {
            path: 'create',
            element: <EmployeeFormPage />,
          },
          {
            path: ':id',
            element: <EmployeeDetailPage />,
          },
          {
            path: ':id/edit',
            element: <EmployeeFormPage />,
          },
        ],
      },
      // Marketplace
      {
        path: 'marketplace',
        children: [
          { index: true, element: <Navigate to="/marketplace/categories" replace /> },
          // Categories
          {
            path: 'categories',
            children: [
              { index: true, element: <CategoriesListPage /> },
              { path: 'create', element: <CategoryFormPage /> },
              { path: ':id/edit', element: <CategoryFormPage /> },
            ],
          },
          // Subcategories
          {
            path: 'subcategories',
            children: [
              { index: true, element: <SubCategoriesListPage /> },
              { path: 'create', element: <SubCategoryFormPage /> },
              { path: ':id/edit', element: <SubCategoryFormPage /> },
            ],
          },
          // Products
          {
            path: 'products',
            children: [
              { index: true, element: <ProductsListPage /> },
              { path: 'create', element: <ProductFormPage /> },
              { path: ':id', element: <ProductDetailPage /> },
              { path: ':id/edit', element: <ProductFormPage /> },
            ],
          },
          // Orders
          {
            path: 'orders',
            children: [
              { index: true, element: <MarketplaceOrdersListPage /> },
              { path: ':id', element: <MarketplaceOrderDetailPage /> },
            ],
          },
          // Payments
          { path: 'payments', element: <MarketplacePaymentsListPage /> },
        ],
      },
      // Settings
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      // System
      {
        path: 'profile',
        element: <ProfilePage />,
      },
    ],
  },

  // Catch all - redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

